"use client";

import { NavbarMain } from "@/components/navbar-main";

import { Toc } from "@/app/[locale]/blog/[slug]/Toc";
import { MDXContent } from "@content-collections/mdx/react";
import { mdxComponents } from "@prose-ui/next";

import { TitleSection } from "@/app/[locale]/blog/TitleSection";
import { Footer } from "@/components/landing/CTAsection";
import { allBlogs } from "content-collections";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

export default function BlogPage({ blog }) {
  const filteredAllBlogs = allBlogs
    .filter((item) => item._meta.fileName !== blog?._meta.fileName)
    .toSorted((a, b) => {
      return a.sort_number - b.sort_number;
    });

  if (!blog) {
    return (
      <div className="mx-auto flex min-h-screen max-w-[90rem] flex-col items-center justify-center px-4 md:px-20">
        <NavbarMain />
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-800">
            Blog not found
          </h1>
          <p className="mt-2 text-gray-600">
            The blog you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {/* Navbar remains at the top with the max width constraint */}
      <div className="mx-auto max-w-[90rem] md:px-20">
        <NavbarMain />
      </div>
      <div className="mt-18">
        {/* Header section */}
        <TitleSection>
          <div className="relative max-w-screen-sm">
            <div className="flex items-center space-x-4">
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-600 shadow-[inset_10px_-50px_94px_0_rgb(199,199,199,0.1)] backdrop-blur transition-all hover:border-gray-300 hover:bg-white/50">
                {blog.category || "Blog Post"}
              </div>
              <div className="text-sm text-gray-500 transition-colors hover:text-gray-800">
                {blog.date}
              </div>
            </div>
            <h1 className="font-display mt-5 text-left text-4xl font-medium text-gray-900 sm:text-4xl sm:leading-[1.25]">
              {blog.title}
            </h1>
            <p className="mt-5 text-gray-500 sm:text-lg">{blog.description}</p>
          </div>
        </TitleSection>

        {/* Main section */}
        <div className="grid-section border-grid-border relative overflow-clip border-y px-4 [.grid-section_~_&]:border-t-0">
          <div className="border-grid-border relative z-auto mx-auto grid max-w-[1080px] grid-cols-3 border-x">
            <div className="relative col-span-3 md:col-span-2">
              <div className="bg-white">
                <div className="w-full overflow-hidden">
                  <Image
                    src={blog.image || "/placeholder.svg?height=400&width=800"}
                    alt={blog.title}
                    width={1200}
                    height={630}
                    className="h-auto w-full object-cover"
                  />
                </div>

                <article className="prose-ui prose-neutral prose-ui-headings:relative prose-ui-headings:scroll-mt-20 prose-ui-headings:font-display prose-ui-a:font-medium prose-ui-a:text-gray-500 prose-ui-a:underline-offset-4 hover:prose-a:text-black prose-ui-thead:text-lg max-w-none px-5 py-10 transition-all sm:px-12">
                  <MDXContent
                    code={blog.content}
                    components={{ ...mdxComponents }}
                  />
                </article>
              </div>
              <div className="border-grid-border border-t p-10 backdrop-blur-lg">
                <div className="flex flex-col gap-y-4">
                  <p className="font-display py-2 text-xl font-medium">
                    Read more
                  </p>
                  <ul className="flex flex-col gap-y-6">
                    {filteredAllBlogs.map((blog, key) => {
                      return <BlogCard key={key} {...blog} />;
                    })}
                  </ul>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="border-grid-border hidden border-l bg-gray-50 p-10 sm:block">
              <div className="flex flex-col gap-y-4 pb-5">
                <p className="text-sm text-gray-500">Written by</p>
                <div className="group flex items-center space-x-3">
                  <Image
                    src={blog.author || "/placeholder.svg?height=40&width=40"}
                    alt="Author"
                    width={40}
                    height={40}
                    className="blur-0 rounded-full transition-all group-hover:brightness-90"
                  />
                  <div className="flex flex-col">
                    <p className="text-sm font-medium whitespace-nowrap text-gray-700">
                      {blog?.author_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {blog?.author_title}
                    </p>
                  </div>
                </div>
              </div>

              <div className="sticky top-16 pt-10 pb-8">
                <Toc sections={blog.toc} />

                <Link
                  href={"/"}
                  target={"_blank"}
                  className="group relative block rounded-xl border border-gray-200 bg-white p-4"
                >
                  <div className="absolute top-2 right-2 z-10 rounded-full border border-gray-200 bg-gradient-to-b from-white/50 to-transparent p-2.5 opacity-0 backdrop-blur-lg transition-opacity group-hover:opacity-100 hover:bg-gray-50">
                    <ArrowRight className="size-4 -rotate-45" />
                  </div>
                  <Image
                    src={"/images/landingPage/hero-image-kabox.png"}
                    alt="Related content"
                    width={300}
                    height={300}
                    className="mb-4 w-full rounded-md"
                  />
                  <h3 className="font-display mt-4 text-lg font-bold">
                    Try Kabox for free
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 underline-offset-4 group-hover:underline">
                    All the tools you need to run your home service business —
                    without the chaos.
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-[90rem]">
        <Footer hasBorder={false} />
      </div>
    </div>
  );
}

function BlogCard({ image, title, description, date, _meta }) {
  return (
    <Link
      href={`/blog/${_meta.path}`}
      className="group flex flex-col items-center gap-4 sm:flex-row"
    >
      <Image
        src={image || "/placeholder.svg"}
        alt={title}
        height={100}
        width={200}
        // fill={true}
        className="blur-0 aspect-video w-full rounded-lg border border-gray-200 object-cover sm:w-[200px]"
      />
      <div className="flex flex-col space-y-2">
        <p className="font-display line-clamp-1 font-medium text-gray-700 underline-offset-4 group-hover:underline">
          {title}
        </p>
        <p className="line-clamp-2 text-sm text-gray-500 underline-offset-2 group-hover:underline">
          {description}
        </p>
        <p className="text-xs text-gray-400 underline-offset-2 group-hover:underline">
          {date}
        </p>
      </div>
    </Link>
  );
}
